import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Grid
} from '@mui/material';
import {
  People,
  TrendingUp,
  Schedule,
  Visibility
} from '@mui/icons-material';
import AnalyticsService, { AnalyticsData } from '../../services/AnalyticsService';

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color?: string;
  subtitle?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ icon, label, value, color = '#1976d2', subtitle }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
    <Box sx={{ mr: 2, color }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ color, fontWeight: 'bold' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const RealtimeMetrics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    // Fetch data immediately
    fetchData();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setError(null);
      const response = await AnalyticsService.fetchDashboardMetrics();
      setData(response);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching realtime metrics:', err);
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value: string | undefined): string => {
    if (!value) return '0';
    const num = parseInt(value);
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const formatDuration = (seconds: string | undefined): string => {
    if (!seconds) return '0s';
    const sec = parseFloat(seconds);
    if (sec >= 60) {
      const minutes = Math.floor(sec / 60);
      const remainingSeconds = Math.floor(sec % 60);
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${Math.floor(sec)}s`;
  };

  const formatPercentage = (value: string | undefined): string => {
    if (!value) return '0%';
    const num = parseFloat(value) * 100;
    return `${num.toFixed(1)}%`;
  };

  const getTimeAgo = (): string => {
    const seconds = Math.floor((new Date().getTime() - lastUpdated.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes === 1) return '1 minute ago';
    return `${minutes} minutes ago`;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          Analytics Overview (7-Day Summary)
        </Typography>
        <Chip
          label={`Updated: ${getTimeAgo()}`}
          size="small"
          color={loading ? 'default' : 'success'}
          variant="outlined"
        />
      </Box>

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {data && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<People sx={{ fontSize: 30 }} />}
              label="Active Users"
              value={formatNumber(data.data.metrics.activeUsers)}
              color="#1976d2"
              subtitle="Unique visitors (7 days)"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<TrendingUp sx={{ fontSize: 30 }} />}
              label="Sessions"
              value={formatNumber(data.data.metrics.sessions)}
              color="#2e7d32"
              subtitle="Last 7 days"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<Schedule sx={{ fontSize: 30 }} />}
              label="Avg Duration"
              value={formatDuration(data.data.metrics.averageSessionDuration)}
              color="#ed6c02"
              subtitle="Per session"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<Visibility sx={{ fontSize: 30 }} />}
              label="Page Views"
              value={formatNumber(data.data.metrics.screenPageViews)}
              color="#9c27b0"
              subtitle="Last 7 days"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      New Users
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatNumber(data.data.metrics.newUsers)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      First-time visitors
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Engagement Rate
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {formatPercentage(data.data.metrics.engagementRate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Engaged sessions
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Pages per Session
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {data.data.metrics.eventsPerSession ?
                        parseFloat(data.data.metrics.eventsPerSession).toFixed(1) : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Average pages viewed
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default RealtimeMetrics;